import { NextSeo, NextSeoProps } from "next-seo";
import { MetaTag } from "next-seo/lib/types";
import { FunctionComponent } from "react";
import { useFragment, graphql } from "react-relay";

import { ArticleSEOContainer_feedContentResource$key } from "@framework/__generated__/relay/ArticleSEOContainer_feedContentResource.graphql";
import usePaginationDetails from "@framework/route/pagination";
import { buildOgImageData } from "@framework/seo/seo.utils";
import { useConfigContext } from "@voxmedia/duet-context/context/config-context";
import { useGlobalDataContext } from "@voxmedia/duet-context/context/global-data-context";
import { getSeoTitle } from "@voxmedia/duet-utils/seo";

interface Props {
  feedContentResource: ArticleSEOContainer_feedContentResource$key;
  /** Override nofollow directive from query */
  nofollow?: boolean;
  hasNextPage?: boolean;
  includeAuthors?: boolean;
  getModifiedTitle?: (t: string | null) => string | null;
}

const ArticleSEOContainer: FunctionComponent<Props> = ({
  hasNextPage = false,
  includeAuthors = true,
  getModifiedTitle = (t) => t,
  ...props
}) => {
  const node = useFragment(
    graphql`
      fragment ArticleSEOContainer_feedContentResource on ContentResourceInterface {
        __typename
        title
        dek {
          plaintext
        }
        robots {
          index
          follow
        }
        permalink
        canonicalUrlOverride
        originalPublishedAt
        publishedAt
        ... on FeedContentResourceInterface {
          # @excludeCacheKey: assignment/unassignment of authors will result in invalidation of the
          # resource cache key, so including the key is not needed
          authors @excludeCacheKey {
            name
          }
        }
        primaryCampaign {
          id
        }
        seo {
          headline
          description
        }
        social {
          headline
          description
          image {
            title
            alt
            originalHeight
            originalWidth
            thumbnails {
              social {
                url
                mimeType
                normalizedRatio
                cropCoordinates {
                  height
                  width
                }
              }
            }
          }
        }
        promo {
          headline
        }

        ledeMedia {
          ... on LedeMediaImageType {
            image {
              title
              alt
              originalHeight
              originalWidth
              thumbnails {
                social {
                  url
                  mimeType
                  normalizedRatio
                  cropCoordinates {
                    height
                    width
                  }
                }
              }
            }
          }
        }

        featuredImage {
          image {
            title
            alt
            originalHeight
            originalWidth
            thumbnails {
              social {
                url
                mimeType
                normalizedRatio
                cropCoordinates {
                  height
                  width
                }
              }
            }
          }
        }

        ... on StreamResourceType {
          streamPublishedAt
        }

        ... on QuickPostResourceType {
          seoArticleBody

          quickAttachment {
            __typename
            ... on CoreImageBlockType {
              image {
                title
                alt
                originalHeight
                originalWidth
                thumbnails {
                  social {
                    url
                    mimeType
                    normalizedRatio
                    cropCoordinates {
                      height
                      width
                    }
                  }
                }
              }
            }
            ... on VideoBlockType {
              video {
                poster {
                  title
                  alt
                  originalHeight
                  originalWidth
                  thumbnails {
                    social {
                      url
                      mimeType
                      normalizedRatio
                      cropCoordinates {
                        height
                        width
                      }
                    }
                  }
                }
              }
            }
            ... on ImageSliderBlockType {
              images {
                image {
                  title
                  alt
                  originalHeight
                  originalWidth
                  thumbnails {
                    social {
                      url
                      mimeType
                      normalizedRatio
                      cropCoordinates {
                        height
                        width
                      }
                    }
                  }
                }
              }
            }
            ... on CoreGalleryBlockType {
              images {
                image {
                  title
                  alt
                  originalHeight
                  originalWidth
                  thumbnails {
                    social {
                      url
                      mimeType
                      normalizedRatio
                      cropCoordinates {
                        height
                        width
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    props.feedContentResource,
  );

  const {
    title,
    permalink,
    canonicalUrlOverride,
    dek,
    robots,
    authors,
    primaryCampaign,
    originalPublishedAt,
    publishedAt,
    seo,
    social,
    ledeMedia,
    featuredImage,
    streamPublishedAt,
  } = node;

  const { placeholderImageUrl } = useGlobalDataContext().siteSettings;

  const noFollow = props.nofollow || !robots.follow || !!primaryCampaign;
  let canonicalUrl = canonicalUrlOverride || permalink;

  const authorTag =
    includeAuthors && authors && authors[0]
      ? {
          name: "author",
          content: authors[0].name,
        }
      : null;

  const seoHeadline = seo.headline;
  const seoDescription =
    (node.__typename === "QuickPostResourceType" && node.seoArticleBody) ||
    seo.description ||
    dek.plaintext;
  const socialHeadline = social.headline;
  const socialDescription =
    social.description || seoDescription || dek.plaintext;

  // TODO: Handle social headline/description
  const ogData: NextSeoProps["openGraph"] = {
    type: "article",
    url: canonicalUrl,
    title: socialHeadline || title,
    description: socialDescription,
    article: {
      publishedTime: originalPublishedAt ? originalPublishedAt : undefined,
      modifiedTime: streamPublishedAt || publishedAt || undefined,
    },
  };

  let image = social.image || ledeMedia?.image || featuredImage?.image || null;

  // For QuickPosts, derive an og:image from the quickAttachment if possible
  if (node.__typename === "QuickPostResourceType") {
    switch (node.quickAttachment?.__typename) {
      case "CoreImageBlockType":
        image = node.quickAttachment.image ?? image;
        break;
      case "VideoBlockType":
        image = node.quickAttachment.video?.poster ?? image;
        break;
      case "CoreGalleryBlockType":
      case "ImageSliderBlockType":
        image = node.quickAttachment.images[0]?.image ?? image;
        break;
    }
  }

  const imageThumbnail = image?.thumbnails.social || null;
  const ogImage = buildOgImageData({
    image,
    imageThumbnail,
    placeholderImageUrl: placeholderImageUrl.social,
  });

  ogData.images = [ogImage];

  const twitterTags: MetaTag[] = [
    {
      name: "twitter:url",
      content: canonicalUrl,
    },
    {
      name: "twitter:title",
      content: socialHeadline || title,
    },
    {
      name: "twitter:description",
      content: socialDescription,
    },
    {
      name: "twitter:image",
      content: ogImage.url,
    },
    ...(ogImage.alt
      ? [
          {
            name: "twitter:image:alt",
            content: ogImage.alt,
          },
        ]
      : []),
  ];

  const {
    pageNumber,
    currentPageHref,
    previousPageHref,
    nextPageHref,
    isPaginating,
  } = usePaginationDetails({
    hasNextPage,
  });

  // pagination/ archives is used on stream landing
  const getArchivesTitle = (title: string | null) =>
    title && isPaginating ? `${title} - Page ${pageNumber}` : title;

  // TODO: add a little compose or pipe function to duet-utils if we keep
  // adding functions here
  const getPageTitle = (title: string | null) => {
    return getArchivesTitle(getModifiedTitle(title));
  };

  // check if there is pagination
  const additionalLinkTags: { rel: string; href: string }[] = [];

  const origin = new URL(String(permalink)).origin;

  // set canonical url to current page
  if (!canonicalUrlOverride && isPaginating) {
    canonicalUrl = `${origin}${currentPageHref}`;
  }

  if (pageNumber > 1) {
    additionalLinkTags.push({
      rel: "prev",
      href: `${origin}${previousPageHref}`,
    });
  }

  if (hasNextPage) {
    additionalLinkTags.push({
      rel: "next",
      href: `${origin}${nextPageHref}`,
    });
  }

  // add noindex to entry if robots.index field is false or password protected
  // TODO: add password protected check
  // const noindex = !robots.index || password !== null;
  const noindex = !robots.index;

  const { communityName } = useConfigContext().communityConfig;

  return (
    <NextSeo
      title={getSeoTitle(
        [getPageTitle(seoHeadline), getPageTitle(title)],
        communityName,
      )}
      description={seoDescription}
      canonical={canonicalUrl}
      openGraph={ogData}
      robotsProps={{
        maxImagePreview: "large",
      }}
      additionalMetaTags={[
        ...(authorTag ? [authorTag] : []),
        ...twitterTags,
        { name: "amazonbot", content: "noarchive" },
      ]}
      nofollow={noFollow}
      additionalLinkTags={additionalLinkTags}
      noindex={noindex}
    />
  );
};

export default ArticleSEOContainer;
